package com.patronat.top.features.venues.presentation

import com.patronat.top.core.base.ViewEvent
import com.patronat.top.core.base.ViewSideEffect
import com.patronat.top.core.base.ViewState
import com.patronat.top.core.domain.models.profiles.User
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.CreateVenueRequest
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueStatus

enum class AddVenueActionMode {
    CREATE_NEW,
    REQUEST_ACCESS,
    AI_IMPORT
}

enum class VenueStatusFilterPreset(
    val id: String,
    val backendStatus: VenueStatus
) {
    ON_MODERATION("on_moderation", VenueStatus.ON_MODERATION),
    APPLICATION("application", VenueStatus.APPLICATION),
    CONNECTED("connected", VenueStatus.CONNECTED),
    REJECTED("rejected", VenueStatus.REJECTED),
    ADDED_BY_MODERATOR("added_by_moderator", VenueStatus.ADDED_BY_MODERATOR),
    CHECK_CHANGES("check_changes", VenueStatus.CHECK_CHANGES),
    CLOSED("closed", VenueStatus.CLOSED),
    DRAFT("draft", VenueStatus.DRAFT);

    companion object {
        fun fromId(id: String): VenueStatusFilterPreset? = entries.firstOrNull { it.id == id }
    }
}

data class VenuesListState(
    val venues: List<VenueResponse> = emptyList(),
    val totalCount: Long = 0,
    val currentPage: Int = 1,
    val pageSize: Int = 20,
    val isLoading: Boolean = false,
    val error: String? = null,
    val searchQuery: String = "",
    val selectedStatusFilterIds: List<String> = emptyList(),
    val expandedVenueMenuId: String? = null,
    val userRole: UserRole? = null,
    val currentUserId: String? = null,
    val ownersById: Map<String, User> = emptyMap(),
    val failedOwnerIds: Set<String> = emptySet(),
    val moderatorsById: Map<String, User> = emptyMap(),
    val failedModeratorIds: Set<String> = emptySet(),
    val isAddVenueDialogVisible: Boolean = false,
    val addVenueActionMode: AddVenueActionMode = AddVenueActionMode.CREATE_NEW,
    val addVenueSearchQuery: String = "",
    val addVenueCandidates: List<VenueResponse> = emptyList(),
    val selectedAddVenueCandidateId: String? = null,
    val isAddVenueCandidatesLoading: Boolean = false,
    val addVenueAiWebsiteUrl: String = "",
    val addVenueAiCreateImmediately: Boolean = false,
    val isAddVenueAiLoading: Boolean = false
) : ViewState {
    val isEmpty: Boolean
        get() = venues.isEmpty() &&
            !isLoading &&
            error == null &&
            searchQuery.isEmpty() &&
            selectedStatusFilterIds.isEmpty()
}

sealed interface VenuesListEvent : ViewEvent {
    data class Initialize(val userRole: UserRole, val currentUserId: String?) : VenuesListEvent
    data object OnReload : VenuesListEvent
    data object OnAddVenueClicked : VenuesListEvent
    data object OnDismissAddVenueDialog : VenuesListEvent
    data class OnAddVenueActionModeChanged(val mode: AddVenueActionMode) : VenuesListEvent
    data class OnAddVenueCandidateQueryChanged(val query: String) : VenuesListEvent
    data class OnAddVenueCandidateSelected(val venueId: String) : VenuesListEvent
    data class OnAddVenueAiWebsiteChanged(val url: String) : VenuesListEvent
    data class OnAddVenueAiCreateImmediatelyChanged(val createImmediately: Boolean) : VenuesListEvent
    data object OnConfirmAddVenueAction : VenuesListEvent
    data class OnSearchChanged(val query: String) : VenuesListEvent
    data class OnStatusFilterChanged(val filterIds: List<String>) : VenuesListEvent
    data class OnPageChanged(val page: Int) : VenuesListEvent
    data class OnPageSizeChanged(val pageSize: Int) : VenuesListEvent
    data class OnVenueActionClicked(val venueId: String) : VenuesListEvent
    data object OnDismissVenueMenu : VenuesListEvent
    data class OnEditVenueClicked(val venueId: String) : VenuesListEvent
    data class OnBlockVenueClicked(val venueId: String) : VenuesListEvent
}

sealed interface VenuesListEffect : ViewSideEffect {
    data object OpenCreateVenue : VenuesListEffect
    data class OpenCreateVenueFromAi(val draft: CreateVenueRequest) : VenuesListEffect
    data class OpenEditVenue(val venueId: String) : VenuesListEffect
}
